import { useEffect, useState } from 'react';
import { Exam, Category, Question, MediaContent, QuestionOption } from '@/types/exam';
import { examsApi } from '@/services/api';
import { getContentLocale, pickLocalizedText } from '@/lib/contentLocale';

/**
 * Backend payload shapes (subset). The backend returns more fields, we map what we need.
 */
interface BackendCategory {
  id: string;
  name: string;
  nameEn?: string | null;
  description?: string;
  descriptionEn?: string | null;
  icon?: string;
  color?: string;
  questionCount?: number;
  subcategories?: BackendCategory[];
}

interface BackendExam {
  id: string;
  name: string;
  nameEn?: string | null;
  description?: string;
  descriptionEn?: string | null;
  icon?: string;
  color?: string;
  bgGradient?: string;
  duration?: number;
  passingScore?: number;
  totalQuestions?: number;
  hasOralQuestions?: boolean;
  hasCodeQuestions?: boolean;
  hasImageQuestions?: boolean;
  hasMathQuestions?: boolean;
  categories?: BackendCategory[];
}

interface BackendQuestionContent {
  type: 'text' | 'audio' | 'image' | 'code' | 'math';
  content: string;
  language?: string;
  displayMode?: 'inline' | 'block';
  altText?: string;
  order?: number;
}

interface BackendQuestionOption {
  type: 'text' | 'code' | 'math' | 'image';
  content: string;
  isCorrect: boolean;
  order?: number;
}

interface BackendQuestion {
  id: string;
  type: 'text' | 'audio' | 'image' | 'code' | 'mixed' | 'math';
  difficulty: 'easy' | 'medium' | 'hard';
  points: number;
  timeLimit?: number;
  explanation?: string;
  contents: BackendQuestionContent[];
  options: BackendQuestionOption[];
}

const mapCategory = (c: BackendCategory): Category => {
  const locale = getContentLocale();
  return {
    id: c.id,
    name: pickLocalizedText(locale, c.name, c.nameEn),
    description: pickLocalizedText(locale, c.description, c.descriptionEn),
  icon: c.icon || '📚',
  color: c.color || 'bg-emerald-500',
  questionCount: c.questionCount ?? 0,
  subcategories: c.subcategories?.map(mapCategory),
  };
};

export const mapExam = (e: BackendExam): Exam => {
  const locale = getContentLocale();
  const categories = (e.categories || []).map(mapCategory);
  return {
    id: e.id,
    name: pickLocalizedText(locale, e.name, e.nameEn),
    description: pickLocalizedText(locale, e.description, e.descriptionEn),
    icon: e.icon || '🎓',
    color: e.color || 'bg-emerald-500',
    bgGradient: e.bgGradient || 'from-emerald-500 to-teal-600',
    categories,
    totalQuestions:
      e.totalQuestions ?? categories.reduce((acc, c) => acc + (c.questionCount || 0), 0),
    duration: e.duration ?? 60,
    passingScore: e.passingScore ?? 70,
    hasOralQuestions: !!e.hasOralQuestions,
    hasCodeQuestions: !!e.hasCodeQuestions,
    hasImageQuestions: !!e.hasImageQuestions,
  };
};

const mapMedia = (c: BackendQuestionContent): MediaContent => ({
  type: c.type,
  content: c.content,
  language: c.language,
  displayMode: c.displayMode === 'block',
  alt: c.altText,
});

const mapOption = (o: BackendQuestionOption): QuestionOption => ({
  content: [{ type: o.type === 'image' ? 'image' : (o.type as MediaContent['type']), content: o.content }],
});

export const mapQuestion = (q: BackendQuestion): Question => {
  const sortedContents = [...(q.contents || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const sortedOptions = [...(q.options || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const correctAnswer = sortedOptions.findIndex((o) => o.isCorrect);
  return {
    id: q.id,
    type: q.type,
    difficulty: q.difficulty,
    content: sortedContents.map(mapMedia),
    options: sortedOptions.map(mapOption),
    correctAnswer: correctAnswer >= 0 ? correctAnswer : 0,
    explanation: q.explanation,
    points: q.points ?? 1,
    timeLimit: q.timeLimit,
  };
};

/**
 * Fetch a single exam by id. Returns null while loading.
 */
export function useExam(examId: string | undefined) {
  const [exam, setExam] = useState<Exam | null>(null);
  const [loading, setLoading] = useState<boolean>(!!examId);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!examId) {
      setExam(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    examsApi
      .getById(examId)
      .then(({ data }) => {
        if (!cancelled) {
          setExam(mapExam(data));
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [examId]);

  return { exam, loading, error };
}

/**
 * Fetch all exams once, return a lookup map keyed by id.
 * Used by ProfilePage to label history entries without N requests.
 */
export function useExamLookup() {
  const [byId, setById] = useState<Record<string, Exam>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    examsApi
      .getAll()
      .then(({ data }) => {
        const list: BackendExam[] = data.items || data || [];
        if (cancelled) return;
        const map: Record<string, Exam> = {};
        list.forEach((e) => {
          map[e.id] = mapExam(e);
        });
        setById(map);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { byId, loading };
}
